'use client';

import useSubsequentEffect from '@carvana/merch-react-hooks/useSubsequentEffect';
import React, { useEffect, useRef } from 'react';
import SectionName from '~/constants/new-cars/SectionName';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import { buildNewCarsHistoryState } from '~/utilities/new-cars/newCarsHistoryState';
import type { ConfiguratorAction } from './configuratorReducer';
import {
  type ConfiguratorState,
  isConfiguratorState,
  type UrlParams
} from './configuratorState';

const MANAGED_PARAMS: Array<keyof UrlParams> = [
  'doors',
  'driveType',
  'engine',
  'exterior',
  'interior',
  'modelVersion',
  'packages',
  'trim',
  'truckBodyStyle',
  'year'
];

function buildUrl(state: ConfiguratorState): string {
  if (isServerSideRendering) return '';

  const url = new URL(window.location.href);
  MANAGED_PARAMS.forEach(p => url.searchParams.delete(p));

  const setParam = (
    key: keyof UrlParams,
    value: string | number | undefined
  ) => {
    if (value != null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  };

  if (state.selectedDoors != null) {
    setParam('doors', state.selectedDoors.doorCount);
  }

  if (state.selectedYear?.year) {
    setParam('year', state.selectedYear.year);
  }

  if (state.selectedModelVersion?.modelVersion) {
    setParam('modelVersion', state.selectedModelVersion.modelVersion);
  }

  if (state.selectedTrim?.trim) {
    setParam('trim', state.selectedTrim.trim);
  }

  if (state.selectedTruckBodyStyle?.truckBodyStyle) {
    setParam('truckBodyStyle', state.selectedTruckBodyStyle.truckBodyStyle);
  }

  if (state.selectedOptions) {
    setParam('driveType', state.selectedOptions.driveType);
    setParam('engine', state.selectedOptions.engineCode);
    setParam('exterior', state.selectedOptions.exteriorColorCode);
    setParam('interior', state.selectedOptions.interiorColorCode);
    state.selectedOptions.packageOptionCodes?.forEach(p =>
      url.searchParams.append('packages', p)
    );
  }

  return url.href;
}

export default function useConfiguratorUrlSync(
  state: ConfiguratorState,
  sectionSequence: SectionName[],
  lastActionRef: React.RefObject<ConfiguratorAction['type'] | null>,
  dispatch: (action: ConfiguratorAction) => void
) {
  const stateRef = useRef(state);
  stateRef.current = state;
  const sectionSequenceRef = useRef(sectionSequence);
  sectionSequenceRef.current = sectionSequence;

  useEffect(() => {
    const s = stateRef.current;
    window.history.replaceState(
      buildNewCarsHistoryState(s, sectionSequenceRef.current),
      '',
      buildUrl(s)
    );
  }, []);

  useSubsequentEffect(() => {
    const actionType = lastActionRef.current;
    lastActionRef.current = null;

    if (actionType === 'RESTORE_FROM_HISTORY') return;

    const historyState = buildNewCarsHistoryState(state, sectionSequence);
    const url = buildUrl(state);

    if (
      actionType === 'SELECT_DOORS' ||
      actionType === 'SELECT_MODEL_VERSION' ||
      actionType === 'SELECT_TRIM' ||
      actionType === 'SELECT_TRUCK_BODY_STYLE' ||
      actionType === 'SELECT_YEAR'
    ) {
      window.history.pushState(historyState, '', url);
    } else {
      window.history.replaceState(historyState, '', url);
    }
  }, [lastActionRef, sectionSequence, state]);

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      const raw = event.state?.newCars;
      const restored = isConfiguratorState(raw) ? raw : null;

      if (!restored) return;

      dispatch({ type: 'RESTORE_FROM_HISTORY', payload: restored });
    }

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [dispatch]);
}
